"use client";

/**
 * Main useAuth Hook
 *
 * Provides unified authentication interface supporting both demo mode
 * and Supabase Auth. Replaces previous Clerk-based implementation.
 *
 * Architecture Rule: AU-01 - Auth Abstraction Layer
 */
import { useEffect, useState } from "react";

import { useRouter } from "next/navigation";

import { useSupabaseAuth } from "@/components/auth/supabase-auth-provider";
import { resetAnalyticsUser } from "@/lib/analytics";
import { clearDemoUserSession, type DemoUser, getDemoUserSession, isDemoMode } from "@/lib/auth/demo-admin";
import { isAdminRole } from "@/lib/utils/user-roles";
import type { Nullable } from "@/types";
import { type OrgRole, OrgType, UserRole } from "@/types/user-roles";

interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  imageUrl?: string;
  title?: string;
  department?: string;
  isDemoUser?: boolean;
}

interface OrganizationInfo {
  id: string;
  name: string;
  slug: string;
  type: OrgType;
  zusEnvironment: "sandbox" | "production";
  role: OrgRole | string | null;
}

// Helper to get role from cookie (for middleware compatibility)
function getRoleFromCookie(): Nullable<UserRole> {
  if (typeof window === "undefined") return null;

  const cookies = document.cookie;
  const roleMatch = cookies.match(/user-role=([^;]+)/);
  if (roleMatch) {
    const role = roleMatch[1] as UserRole;
    // Validate it's a real UserRole
    if (Object.values(UserRole).includes(role)) {
      return role;
    }
  }
  return null;
}

export function useAuth() {
  const {
    user: supabaseUserData,
    isLoaded,
    isSignedIn,
    activeOrganization,
    signOut: supabaseSignOut,
  } = useSupabaseAuth();
  const router = useRouter();
  const [demoUser, setDemoUser] = useState<Nullable<DemoUser>>(null);
  const [isDemo, setIsDemo] = useState(false);
  const [cookieRole, setCookieRole] = useState<Nullable<UserRole>>(null);

  // Check for demo authentication on mount and when storage changes
  useEffect(() => {
    const checkDemoAuth = () => {
      const demo = isDemoMode();
      const demoUserData = getDemoUserSession();
      setIsDemo(demo);
      setDemoUser(demoUserData);
      // Also check cookie role for Supabase users
      setCookieRole(getRoleFromCookie());
    };

    checkDemoAuth();

    // Listen for storage changes and custom demo auth events
    const handleStorageChange = () => {
      checkDemoAuth();
    };

    const handleDemoAuthChange = () => {
      checkDemoAuth();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("demo-auth-changed", handleDemoAuthChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("demo-auth-changed", handleDemoAuthChange);
    };
  }, []);

  // Determine the effective role for Supabase users
  // Priority: 1) User role from database, 2) user-role cookie, 3) PROVIDER default
  const getSupabaseUserRole = (): UserRole => {
    // First check user data from Supabase auth context
    if (supabaseUserData?.role) {
      const role = supabaseUserData.role as UserRole;
      if (Object.values(UserRole).includes(role)) {
        return role;
      }
    }

    // Check if user is Lyfe Admin
    if (supabaseUserData?.isLyfeAdmin) {
      return UserRole.LYFE_ADMIN;
    }

    // Fall back to cookie role (set during demo login)
    if (cookieRole) {
      return cookieRole;
    }

    // Default to PROVIDER
    return UserRole.PROVIDER;
  };

  // Get current organization info
  const getOrganizationInfo = (): Nullable<OrganizationInfo> => {
    if (!activeOrganization || !isLoaded) return null;

    return {
      id: activeOrganization.id,
      name: activeOrganization.name,
      slug: activeOrganization.slug || "",
      type: (activeOrganization.type as OrgType) || OrgType.PRODUCTION,
      zusEnvironment: "sandbox", // Default to sandbox for safety
      role: activeOrganization.role || null,
    };
  };

  // Transform demo user or Supabase user to our User interface
  const user: Nullable<User> = demoUser
    ? {
        id: demoUser.id,
        email: demoUser.email,
        name: `${demoUser.firstName} ${demoUser.lastName}`,
        role: demoUser.role,
        imageUrl: demoUser.avatar,
        title: demoUser.title,
        department: demoUser.department,
        isDemoUser: true,
      }
    : supabaseUserData
      ? {
          id: supabaseUserData.id,
          email: supabaseUserData.email || "",
          name: supabaseUserData.name || "Unknown User",
          role: getSupabaseUserRole(),
          imageUrl: undefined,
          isDemoUser: false,
        }
      : null;

  const signOut = async () => {
    try {
      resetAnalyticsUser();
      // Clear demo session
      clearDemoUserSession();
      // Clear cookies
      document.cookie = "demo-auth=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      document.cookie = "demo-user=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      document.cookie = "user-role=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      setDemoUser(null);
      setIsDemo(false);

      // Use Supabase sign out
      await supabaseSignOut();

      router.push("/login");
    } catch (_error) {}
  };

  const hasRole = (requiredRole: UserRole): boolean => {
    if (!user) return false;
    return user.role === requiredRole || isAdminRole(user.role);
  };

  const isAuthenticated = (isDemo && !!demoUser) || (isSignedIn && !!supabaseUserData);
  const isLoading = !isLoaded && !isDemo;
  const organizationInfo = getOrganizationInfo();

  // Check if user is a Lyfe Admin (either by role or isLyfeAdmin flag from database)
  const isLyfeAdmin = user?.role === UserRole.LYFE_ADMIN || supabaseUserData?.isLyfeAdmin === true;

  // Check if current organization is a demo organization
  const isDemoOrganization = organizationInfo?.type === OrgType.DEMO;

  return {
    user,
    isLoading,
    isAuthenticated,
    signOut,
    hasRole,
    isDemoMode: isDemo,
    // Organization context
    organization: organizationInfo,
    hasOrganization: !!organizationInfo,
    isLyfeAdmin,
    isDemoOrganization,
    // Environment helpers
    zusEnvironment: organizationInfo?.zusEnvironment || "sandbox",
  };
}
